import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { Bold, Italic, Underline, AlignRight, AlignCenter, AlignLeft, AlignJustify, List, ListOrdered, Undo2, Redo2, Table2, Eraser } from "lucide-react";

const B = ({ title, onDo, children, testid }) => (
  <button type="button" title={title} data-testid={testid} onMouseDown={(e) => { e.preventDefault(); onDo(); }}
    className="h-8 min-w-8 px-1.5 flex items-center justify-center rounded-md text-slate-600 hover:bg-slate-200/70 hover:text-slate-900 transition-colors">
    {children}
  </button>
);

const TABLE = `<table style="width:100%;border-collapse:collapse" border="1"><tr><td style="padding:6px">&nbsp;</td><td style="padding:6px">&nbsp;</td><td style="padding:6px">&nbsp;</td></tr><tr><td style="padding:6px">&nbsp;</td><td style="padding:6px">&nbsp;</td><td style="padding:6px">&nbsp;</td></tr></table><p></p>`;

// Word-like editor rendered as an A4 sheet; parent controls content via ref.setHTML / ref.getHTML.
export const DocEditor = forwardRef(({ initialHTML = "", header = null, footer = null, compact = false }, ref) => {
  const area = useRef(null);
  const sel = useRef(null);
  const keep = () => { const r = window.getSelection(); if (r?.rangeCount && area.current?.contains(r.anchorNode)) sel.current = r.getRangeAt(0).cloneRange(); };
  useEffect(() => { if (area.current) area.current.innerHTML = initialHTML; }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useImperativeHandle(ref, () => ({
    getHTML: () => area.current?.innerHTML || "",
    setHTML: (h) => { if (area.current) area.current.innerHTML = h; },
    insertHTML: (h) => {
      area.current?.focus();
      const r = window.getSelection();
      if (sel.current) { r.removeAllRanges(); r.addRange(sel.current); } else { r.selectAllChildren(area.current); r.collapseToEnd(); }
      document.execCommand("insertHTML", false, h); keep();
    },
  }));
  const ex = (c, v = null) => { document.execCommand(c, false, v); area.current?.focus(); };
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-100 overflow-hidden" data-testid="doc-editor">
      <div className="sticky top-0 z-10 flex flex-wrap items-center gap-0.5 border-b border-slate-200 bg-white px-2 py-1">
        <select data-testid="doc-editor-size" className="h-8 rounded-md border border-slate-200 bg-white px-1 text-xs"
          onChange={(e) => { ex("fontSize", e.target.value); e.target.value = ""; }} defaultValue="">
          <option value="" disabled>الحجم</option><option value="2">صغير</option><option value="3">عادي</option><option value="4">كبير</option><option value="5">أكبر</option><option value="6">عنوان</option>
        </select>
        <B title="عريض" testid="doc-bold" onDo={() => ex("bold")}><Bold className="h-4 w-4" /></B>
        <B title="مائل" testid="doc-italic" onDo={() => ex("italic")}><Italic className="h-4 w-4" /></B>
        <B title="تسطير" testid="doc-underline" onDo={() => ex("underline")}><Underline className="h-4 w-4" /></B>
        <span className="mx-1 h-5 w-px bg-slate-200" />
        <B title="يمين" onDo={() => ex("justifyRight")}><AlignRight className="h-4 w-4" /></B>
        <B title="وسط" onDo={() => ex("justifyCenter")}><AlignCenter className="h-4 w-4" /></B>
        <B title="يسار" onDo={() => ex("justifyLeft")}><AlignLeft className="h-4 w-4" /></B>
        <B title="ضبط" onDo={() => ex("justifyFull")}><AlignJustify className="h-4 w-4" /></B>
        <span className="mx-1 h-5 w-px bg-slate-200" />
        <B title="قائمة مرقمة" onDo={() => ex("insertOrderedList")}><ListOrdered className="h-4 w-4" /></B>
        <B title="قائمة نقطية" onDo={() => ex("insertUnorderedList")}><List className="h-4 w-4" /></B>
        <B title="جدول" testid="doc-table" onDo={() => ex("insertHTML", TABLE)}><Table2 className="h-4 w-4" /></B>
        <span className="mx-1 h-5 w-px bg-slate-200" />
        <B title="تراجع" onDo={() => ex("undo")}><Undo2 className="h-4 w-4" /></B>
        <B title="إعادة" onDo={() => ex("redo")}><Redo2 className="h-4 w-4" /></B>
        <B title="إزالة التنسيق" onDo={() => ex("removeFormat")}><Eraser className="h-4 w-4" /></B>
      </div>
      <div className={`${compact ? "max-h-[38vh] p-2 sm:p-3" : "max-h-[60vh] p-3 sm:p-6"} overflow-y-auto`}>
        <div className={`mx-auto w-full max-w-[794px] bg-white shadow-md rounded-sm ${compact ? "min-h-[160px] px-4 py-4" : "min-h-[700px] px-6 sm:px-12 py-8"}`}>
          {header}
          <div ref={area} contentEditable suppressContentEditableWarning dir="rtl" data-testid="doc-editor-area" onKeyUp={keep} onMouseUp={keep} onInput={keep}
            className={`${compact ? "min-h-[120px]" : "min-h-[380px]"} text-[15px] leading-[2.1] text-slate-800 focus:outline-none [&_ol]:list-decimal [&_ol]:pr-6 [&_ul]:list-disc [&_ul]:pr-6 [&_td]:border [&_td]:border-slate-300`} />
          {footer}
        </div>
      </div>
    </div>
  );
});
